import {
    ISelectedAppointmentServices,
    IVisitRequestSelectionState,
    useBulkAppointmentServicesSelection,
} from '@/components/bulkQueue/cells/services/useBulkAppointmentServicesSelection';
import { BulkState } from '@/components/bulkQueue/tables/bulkContextUtils';
import { ServicesTableRow } from '@/components/bulkQueue/types';
import type { MutationCallbacks } from '@/fetching/graphql/mutationCallbacks';
import { ReactNode, createContext, useContext, useMemo } from 'react';

// BulkServicesContext does NOT extend BulkSelectionContext because services
// use focus-based editing (one row focused, compatible rows opted-in) rather
// than the per-entity boolean selection that clinician and date contexts use.
// The selection lifecycle is driven by focusedVisitRequest state, not a store.

type BulkServicesContextValue = {
    state: BulkState;
    submit: (options?: MutationCallbacks) => Promise<void>;
    selectedCount: number;
    entityCount: number;
    summaryString: string;
    focusedVisitRequest: ServicesTableRow | null;
    hasFocusedVisitRequest: boolean;
    cancelFocusedEditing: () => void;
    getSelectionState: (row: ServicesTableRow) => IVisitRequestSelectionState;
    selectedAppointmentServices: ISelectedAppointmentServices;
    numSelectedAppointmentServices: number;
};

const BulkServicesContext = createContext<BulkServicesContextValue | null>(null);

type BulkServicesContextProviderProps = {
    children: ReactNode;
    entities: ServicesTableRow[];
};

export const BulkServicesContextProvider = (props: BulkServicesContextProviderProps) => {
    const { children, entities } = props;
    const selection = useBulkAppointmentServicesSelection();

    const value: BulkServicesContextValue = useMemo(
        () => ({
            state: selection.mutation.loading ? 'submitting' : 'idle',
            submit: selection.mutation.commitSelectedServiceAssignments,
            selectedCount: selection.numSelectedVisitRequests,
            entityCount: entities.length,
            summaryString: selection.mutation.summaryString,
            focusedVisitRequest: selection.focusedVisitRequest,
            hasFocusedVisitRequest: selection.hasFocusedVisitRequest,
            cancelFocusedEditing: selection.cancelFocusedEditing,
            getSelectionState: selection.getSelectionState,
            selectedAppointmentServices: selection.selectedAppointmentServices,
            numSelectedAppointmentServices: selection.numSelectedAppointmentServices,
        }),
        [selection, entities.length],
    );

    return <BulkServicesContext.Provider value={value}>{children}</BulkServicesContext.Provider>;
};

export const useBulkServicesContext = () => {
    const context = useContext(BulkServicesContext);
    if (!context) {
        throw new Error('useBulkServicesContext must be used within a BulkServicesContextProvider');
    }
    return context;
};
